import {
  AspectRatio,
  Box,
  Button,
  Chip,
  Divider,
  Sheet,
  Table,
  Typography,
} from "@mui/joy";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { theme } from "../../components/Theme";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { LockOpenIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { ArrowOutward } from "@mui/icons-material";
import { useAuthStore } from "../../store/session";

const Upgrade = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { session } = useAuthStore();

  const features = [
    {
      featureName: "Intuitive Dashboard",
      seller: true,
      buyer: false,
    },
    {
      featureName: "View Supplier Catalogue",
      seller: true,
      buyer: true,
    },
    {
      featureName: "Buy from Other Supplier",
      seller: false,
      buyer: true,
    },
    {
      featureName: "Create PO/SO form",
      seller: true,
      buyer: t("upgradeAccount.accountComparison.features.3.buyer"),
    },
    {
      featureName: "See Order List",
      seller: true,
      buyer: true,
    },
    {
      featureName: "See Order Reports",
      seller: true,
      buyer: false,
    },
    {
      featureName: "Manage Inventory",
      seller: true,
      buyer: false,
    },
    {
      featureName: "Create Catalogue to Sell",
      seller: true,
      buyer: false,
    },
    {
      featureName: "BizPaylater",
      seller: true,
      buyer: false,
    },
    {
      featureName: "Set up few companies within an account",
      seller: true,
      buyer: t("upgradeAccount.accountComparison.features.9.buyer"),
    },
    {
      featureName: "Manage employee",
      seller: true,
      buyer: false,
    },
    {
      featureName: "Get Credits for New Account",
      seller: t("upgradeAccount.accountComparison.features.11.seller"),
      buyer: false,
    },
    {
      featureName: "Referral",
      seller: true,
      buyer: true,
    },
    {
      featureName: "Business Contact",
      seller: true,
      buyer: false,
    },
    {
      featureName: "Pricing",
      seller: t("upgradeAccount.accountComparison.features.14.seller"),
      buyer: t("upgradeAccount.accountComparison.features.14.buyer"),
    },
  ];

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        p: { xs: 0, md: "32px" },
        pb: 0,
      }}
    >
      <Stack
        direction={"column"}
        sx={{
          gap: "24px",
        }}
      >
        {/* page title */}
        <Stack direction={"column"} sx={{ gap: "16px" }}>
          <Typography
            level="text-xl3-bold"
            textColor={theme.palette.common.black}
          >
            {t("upgradeAccount.pageTitle")}
          </Typography>

          <Typography
            level="text-md-medium"
            textColor={theme.palette.common.black}
          >
            {t("upgradeAccount.pageSubtitle")}
          </Typography>
        </Stack>

        {/* features */}
        <Box component={"section"}>
          {/* feature 1 */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            sx={{
              py: "32px",
              justifyContent: "space-between",
              gap: "50px",
            }}
          >
            {/* description */}
            <Stack
              direction={"column"}
              sx={{
                gap: "16px",
                width: { xs: "100%", md: "45%" },
                justifyContent: "center",
              }}
            >
              <Typography
                level="text-md-semibold"
                textColor={theme.palette.primary[500]}
              >
                {t("upgradeAccount.features.0.preface")}
              </Typography>

              <Typography
                level="text-xl2-bold"
                textColor={theme.palette.common.black}
              >
                {t("upgradeAccount.features.0.title")}
              </Typography>

              <Typography
                level="text-md-medium"
                textColor={theme.palette.common.black}
              >
                {t("upgradeAccount.features.0.description")}
              </Typography>

              <Typography
                level="text-sm-bold"
                textColor={theme.palette.primary[500]}
                endDecorator={<ArrowOutward />}
                sx={{
                  display: { xs: "flex", md: "none" },
                }}
              >
                <Link
                  to="https://www.linkzasia.com/id-ID/features?&dashboard=0"
                  target="_blank"
                >
                  {t("upgradeAccount.learnMore")}
                </Link>
              </Typography>
            </Stack>

            <AspectRatio sx={{ width: { xs: "345px", md: "600px" } }}>
              <video src={"/animations/unified_dashboard.webm"} autoPlay loop />
            </AspectRatio>
          </Stack>

          <Divider sx={{ width: "100%" }} />

          {/* feature 2 */}
          <Stack
            direction={{ xs: "column", md: "row-reverse" }}
            sx={{
              py: "32px",
              justifyContent: "space-between",
              gap: "50px",
            }}
          >
            {/* description */}
            <Stack
              direction={"column"}
              sx={{
                gap: "16px",
                width: { xs: "100%", md: "45%" },
                justifyContent: "center",
                textAlign: { xs: "left", md: "right" },
              }}
            >
              <Typography
                level="text-md-semibold"
                textColor={theme.palette.primary[500]}
              >
                {t("upgradeAccount.features.1.preface")}
              </Typography>

              <Typography
                level="text-xl2-bold"
                textColor={theme.palette.common.black}
              >
                {t("upgradeAccount.features.1.title")}
              </Typography>

              <Typography
                level="text-md-medium"
                textColor={theme.palette.common.black}
              >
                {t("upgradeAccount.features.1.description")}
              </Typography>

              <Typography
                level="text-sm-bold"
                textColor={theme.palette.primary[500]}
                endDecorator={<ArrowOutward />}
                sx={{
                  display: { xs: "flex", md: "none" },
                }}
              >
                <Link
                  to="https://www.linkzasia.com/id-ID/features?&catalog=0"
                  target="_blank"
                >
                  {t("upgradeAccount.learnMore")}
                </Link>
              </Typography>
            </Stack>

            <AspectRatio sx={{ width: { xs: "345px", md: "600px" } }}>
              <video
                src={"/animations/supplier_and_catalog.webm"}
                autoPlay
                loop
              />
            </AspectRatio>
          </Stack>

          <Divider sx={{ width: "100%" }} />

          {/* feature 3 */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            sx={{
              py: "32px",
              justifyContent: "space-between",
              gap: "50px",
            }}
          >
            {/* description */}
            <Stack
              direction={"column"}
              sx={{
                gap: "16px",
                width: { xs: "100%", md: "45%" },
                justifyContent: "center",
              }}
            >
              <Typography
                level="text-md-semibold"
                textColor={theme.palette.primary[500]}
              >
                {t("upgradeAccount.features.2.preface")}
              </Typography>

              <Typography
                level="text-xl2-bold"
                textColor={theme.palette.common.black}
              >
                {t("upgradeAccount.features.2.title")}
              </Typography>

              <Typography
                level="text-md-medium"
                textColor={theme.palette.common.black}
              >
                {t("upgradeAccount.features.2.description")}
              </Typography>

              <Typography
                level="text-sm-bold"
                textColor={theme.palette.primary[500]}
                endDecorator={<ArrowOutward />}
                sx={{
                  display: { xs: "flex", md: "none" },
                }}
              >
                <Link
                  to="https://www.linkzasia.com/id-ID/features?&order=2"
                  target="_blank"
                >
                  {t("upgradeAccount.learnMore")}
                </Link>
              </Typography>
            </Stack>

            <AspectRatio sx={{ width: { xs: "345px", md: "600px" } }}>
              <video src={"/animations/invoicing.webm"} autoPlay loop />
            </AspectRatio>
          </Stack>
        </Box>

        {/* account compare */}
        <Stack direction={"column"} gap={"24px"}>
          <Typography
            level="text-xl2-bold"
            textColor={theme.palette.common.black}
          >
            {t("upgradeAccount.accountComparison.title")}
          </Typography>

          {/* features table */}
          <Table
            sx={{
              "& th": {
                textAlign: "center",
                py: "16px",
                borderBottom: `2px solid #F1F8F4 !important`,
                whiteSpace: { xs: "wrap", md: "nowrap" },
              },
              "& thead th:nth-child(1)": {
                textAlign: "left",
                pl: { xs: "4px", md: "82px" },
              },
              "& thead th:nth-child(2)": {
                borderWidth: "2px",
                borderBottom: `2px solid #F1F8F4 !important`,
                borderColor: theme.palette.primary[500],
                backgroundColor: theme.palette.primary[50],
                boxShadow: "0px 1px 3px 0px #0000001A",
              },
              "& tbody td": {
                borderBottom: `2px solid #F1F8F4 !important`,
                py: "16px",
              },
              "& tbody td:nth-child(1)": {
                pl: { xs: "4px", md: "82px" },
              },
              "& tbody td:nth-child(2)": {
                borderWidth: "2px",
                borderBottom: `2px solid #F1F8F4 !important`,
                borderTop: 0,
                borderColor: theme.palette.primary[500],
                backgroundColor: theme.palette.primary[50],
                boxShadow: "0px 1px 3px 0px #0000001A",
              },
              "& tr:last-child": {
                "& td:nth-child(2)": {
                  borderWidth: "2px",
                  borderBottom: `2px solid ${theme.palette.primary[500]} !important`,
                  borderTop: 0,
                  borderColor: theme.palette.primary[500],
                  backgroundColor: theme.palette.primary[50],
                  //   boxShadow: "0px 1px 3px 0px #0000001A",
                },
              },
            }}
          >
            <thead>
              <tr>
                <th>
                  <Typography
                    level="text-md-bold"
                    textColor={theme.palette.primary[500]}
                  >
                    {t(
                      "upgradeAccount.accountComparison.tableHead.featureName"
                    )}
                  </Typography>
                </th>
                <th>
                  <Typography
                    level="text-md-bold"
                    textColor={theme.palette.primary[500]}
                  >
                    {t("upgradeAccount.accountComparison.tableHead.seller")}
                  </Typography>
                </th>
                <th>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    sx={{
                      alignItems: "center",
                      justifyContent: "center",
                      gap: { xs: "8px", md: "16px" },
                    }}
                  >
                    <Typography
                      level="text-md-bold"
                      textColor={theme.palette.common.black}
                    >
                      {t("upgradeAccount.accountComparison.tableHead.buyer")}
                    </Typography>{" "}
                    <Chip variant="outlined" color="primary">
                      {t("upgradeAccount.accountComparison.tableHead.current")}
                    </Chip>
                  </Stack>
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr key={feature.featureName}>
                  <td>
                    <Typography
                      level={
                        index === features.length - 1
                          ? "text-sm-bold"
                          : "text-sm-medium"
                      }
                    >
                      {t(
                        `upgradeAccount.accountComparison.features.${index}.feature`
                      )}
                    </Typography>
                  </td>
                  <td>
                    <Stack
                      direction={"row"}
                      sx={{
                        textAlign: "center",
                        justifyContent: "center",
                      }}
                    >
                      {typeof feature.seller === "string" ? (
                        <Typography
                          level={
                            index === features.length - 1
                              ? "text-sm-bold"
                              : "text-sm-medium"
                          }
                        >
                          {feature.seller}
                        </Typography>
                      ) : (
                        <CheckCircleIcon
                          width={20}
                          height={20}
                          color={theme.palette.primary[500]}
                        />
                      )}
                    </Stack>
                  </td>
                  <td>
                    <Stack
                      direction={"row"}
                      sx={{
                        textAlign: "center",
                        justifyContent: "center",
                      }}
                    >
                      {typeof feature.buyer === "string" ? (
                        <Typography
                          level={
                            index === features.length - 1
                              ? "text-sm-bold"
                              : "text-sm-medium"
                          }
                        >
                          {feature.buyer}
                        </Typography>
                      ) : feature.buyer ? (
                        <CheckCircleIcon
                          width={20}
                          height={20}
                          color={theme.palette.primary[500]}
                        />
                      ) : (
                        <XCircleIcon
                          width={20}
                          height={20}
                          color={theme.palette.danger[500]}
                        />
                      )}
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Stack>

        {/* confirm button */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            position: "relative",
            py: "24px",
            px: "32px",
            gap: "16px",
          }}
        >
          <Divider
            sx={{
              width: { xs: "100dvw", md: "90dvw" },
              position: "absolute",
              left: { xs: "-16px", md: "-160px" },
              top: 0,
              backgroundColor: theme.palette.others.disabled,
            }}
          />

          <Typography
            level="text-xl2-bold"
            textColor={theme.palette.common.black}
          >
            {t("upgradeAccount.accountComparison.footerTitle")}
          </Typography>

          <Button
            id="upgrade-submit-desktop"
            startDecorator={<LockOpenIcon width={20} />}
            onClick={() => {
              navigate(
                `/client/upgrade-account/welcome/${session?.activeBusiness.id}`
              );
            }}
            sx={{
              my: "auto",
              minWidth: "100px",
            }}
          >
            {t("upgradeAccount.accountComparison.unlockBtn")}
          </Button>
        </Stack>
      </Stack>
    </Sheet>
  );
};

export default Upgrade;
